/**
 * Schedules Service - Irrigo Platform
 * Servicio para gestión de programaciones de riego
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type { 
  ApiSchedule, 
  CreateScheduleRequest, 
  UpdateScheduleRequest,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

export interface ScheduleFilters extends PaginationParams {
  gardenId?: string;
  companyId?: string; // Solo superadmin puede filtrar por empresa
  enabled?: boolean;
}

// ============================================================================
// SCHEDULES SERVICE
// ============================================================================

export const schedulesService = {
  /**
   * Listar programaciones
   */
  async getAll(params?: ScheduleFilters): Promise<PaginatedResponse<ApiSchedule>> {
    return api.getPaginated<ApiSchedule>('/schedules', params);
  },

  /**
   * Obtener programación por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiSchedule>> {
    return api.get<ApiSchedule>(`/schedules/${id}`);
  },

  /**
   * Crear nueva programación
   */
  async create(data: CreateScheduleRequest): Promise<ApiResponse<ApiSchedule>> {
    return api.post<ApiSchedule>('/schedules', data);
  },

  /**
   * Actualizar programación
   */
  async update(id: string, data: UpdateScheduleRequest): Promise<ApiResponse<ApiSchedule>> {
    return api.put<ApiSchedule>(`/schedules/${id}`, data);
  },

  /**
   * Activar/desactivar programación
   * Si enabled no se proporciona, el backend hace auto-toggle
   */
  async toggle(id: string, enabled?: boolean): Promise<ApiResponse<ApiSchedule>> {
    const body = enabled !== undefined ? { enabled } : {};
    return api.put<ApiSchedule>(`/schedules/${id}/toggle`, body);
  },

  /**
   * Eliminar programación
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/schedules/${id}`);
  },
};

export default schedulesService;
